import { useEffect, useRef } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import {
  Bold, Italic, Underline, Strikethrough, List, ListOrdered,
  AlignLeft, AlignCenter, AlignRight, Link as LinkIcon, Image as ImageIcon, Quote, Heading2, Heading3, RemoveFormatting,
} from "lucide-react";

function cmd(command, value) {
  document.execCommand(command, false, value);
}

function ToolbarButton({ onClick, title, children, testid }) {
  return (
    <button
      type="button"
      title={title}
      data-testid={testid}
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      className="rounded-md p-1.5 text-slate-600 hover:bg-slate-200 hover:text-slate-900"
    >
      {children}
    </button>
  );
}

export default function RichTextEditor({ value, onChange, placeholder = "Sayfa içeriğini yazın..." }) {
  const ref = useRef(null);
  const lastRef = useRef(value || "");
  const fileRef = useRef(null);

  useEffect(() => {
    if (!ref.current) return;
    const incoming = value || "";
    if (incoming !== ref.current.innerHTML) {
      ref.current.innerHTML = incoming;
      lastRef.current = incoming;
    }
  }, [value]);

  const emit = () => {
    if (!ref.current) return;
    const html = ref.current.innerHTML;
    lastRef.current = html;
    onChange?.(html);
  };

  const insertLink = () => {
    const url = window.prompt("Bağlantı URL");
    if (!url) return;
    cmd("createLink", url);
    emit();
  };

  const insertImage = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/pages/upload-image", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = data.url?.startsWith("http") ? data.url : `${API.replace("/api", "")}${data.url}`;
      ref.current?.focus();
      cmd("insertImage", url);
      emit();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Görsel yüklenemedi");
    }
  };

  return (
    <div className="rich-editor overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid="rich-text-editor">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-slate-200 bg-slate-50 px-2 py-1.5">
        <ToolbarButton title="Başlık 2" onClick={() => { cmd("formatBlock", "h2"); emit(); }}><Heading2 className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton title="Başlık 3" onClick={() => { cmd("formatBlock", "h3"); emit(); }}><Heading3 className="h-4 w-4" /></ToolbarButton>
        <span className="mx-1 h-5 w-px bg-slate-200" />
        <ToolbarButton title="Kalın" onClick={() => { cmd("bold"); emit(); }}><Bold className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton title="İtalik" onClick={() => { cmd("italic"); emit(); }}><Italic className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton title="Altı çizili" onClick={() => { cmd("underline"); emit(); }}><Underline className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton title="Üstü çizili" onClick={() => { cmd("strikeThrough"); emit(); }}><Strikethrough className="h-4 w-4" /></ToolbarButton>
        <span className="mx-1 h-5 w-px bg-slate-200" />
        <ToolbarButton title="Madde işaretleri" onClick={() => { cmd("insertUnorderedList"); emit(); }}><List className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton title="Numaralı liste" onClick={() => { cmd("insertOrderedList"); emit(); }}><ListOrdered className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton title="Alıntı" onClick={() => { cmd("formatBlock", "blockquote"); emit(); }}><Quote className="h-4 w-4" /></ToolbarButton>
        <span className="mx-1 h-5 w-px bg-slate-200" />
        <ToolbarButton title="Sola" onClick={() => { cmd("justifyLeft"); emit(); }}><AlignLeft className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton title="Ortala" onClick={() => { cmd("justifyCenter"); emit(); }}><AlignCenter className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton title="Sağa" onClick={() => { cmd("justifyRight"); emit(); }}><AlignRight className="h-4 w-4" /></ToolbarButton>
        <span className="mx-1 h-5 w-px bg-slate-200" />
        <ToolbarButton title="Bağlantı" onClick={insertLink}><LinkIcon className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton title="Görsel" onClick={insertImage} testid="editor-image"><ImageIcon className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton title="Temizle" onClick={() => { cmd("removeFormat"); emit(); }}><RemoveFormatting className="h-4 w-4" /></ToolbarButton>
      </div>
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={onFile} />
      <div
        ref={ref}
        className="page-content min-h-[280px] px-4 py-3 outline-none empty:before:pointer-events-none empty:before:text-slate-400 empty:before:content-[attr(data-placeholder)]"
        contentEditable
        data-placeholder={placeholder}
        data-testid="rich-text-body"
        suppressContentEditableWarning
        onInput={emit}
      />
    </div>
  );
}
